import type { Mixer } from './mixer';
import { ringTick } from './synth';

/**
 * The lock-up yard's own soundscape while the profile page is up (src/profile): drizzle on the corrugated
 * roof of the lock-up with the odd drip off its edge, a distant traffic hum that swells and recedes, the sodium
 * lamp's buzz, and — now and then — a dog two streets off or a far siren (the city's events, played through
 * `events`), and the yard's cat (profileScene.ts), faint, from the far side of the yard. The muffled radio inside the lock-up is the car radio tuned through the mixer's music muffle
 * (audioSystem.ts `yard`), so it respects the music volume as the ambience here respects the sfx volume.
 *
 * Everything is synthesised on the mixer's noise loops and oscillators; the beds fade in over a second and a half
 * when the page opens and out over a second when it closes. Non-deterministic (Math.random), like the rest of the
 * audio.
 */
export type YardEvent = 'dog' | 'farSiren';

const r = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

export class YardAmbience {
  private nodes: AudioNode[] = [];
  private level: GainNode | null = null;
  private drips: GainNode | null = null;
  private on = false;
  private eventClock = r(5, 11);
  private dripClock = r(0.3, 1.2);
  private catClock = r(9, 24);
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  /** The last event asked for (diagnostics). */
  lastEvent: YardEvent | null = null;

  constructor(
    private readonly mx: Mixer,
    private readonly events: (e: YardEvent) => void,
  ) {}

  get active(): boolean {
    return this.on;
  }

  private build(): void {
    const mx = this.mx;
    const ctx = mx.ctx as AudioContext;
    const t = ctx.currentTime;
    const reg = <T extends AudioNode>(n: T): T => {
      this.nodes.push(n);
      return n;
    };
    const gain = (v: number): GainNode => {
      const g = reg(ctx.createGain());
      g.gain.value = v;
      return g;
    };
    const filt = (type: BiquadFilterType, f: number, q = 0.7): BiquadFilterNode => {
      const b = reg(ctx.createBiquadFilter());
      b.type = type;
      b.frequency.value = f;
      b.Q.value = q;
      return b;
    };
    const lfo = (hz: number, depth: number, target: AudioParam, type: OscillatorType = 'sine'): void => {
      const o = reg(ctx.createOscillator());
      o.type = type;
      o.frequency.value = hz;
      const d = gain(depth);
      o.connect(d).connect(target);
      o.start(t);
    };
    const src = (kind: 'white' | 'pink' | 'brown'): AudioBufferSourceNode => {
      const s = reg(mx.noiseSource(kind));
      mx.startNoise(s, t);
      return s;
    };
    const level = gain(0);
    level.connect(mx.buses.ambience);
    this.level = level;

    // Distant traffic over the rooftops: a low rumble that swells and recedes (the swell is a unity stage so it
    // scales with the level).
    const traffic = gain(0.17);
    const swell = gain(1);
    src('brown').connect(filt('lowpass', 160)).connect(swell).connect(traffic).connect(level);
    lfo(0.06, 0.18, swell.gain);
    lfo(0.027, 0.1, swell.gain);

    // Drizzle on the corrugated iron: a bright band with a fast irregular flutter, and a softer wash on the yard.
    const tin = gain(0.05);
    const tinAm = gain(0.7);
    src('white').connect(filt('bandpass', 2300, 0.9)).connect(tinAm).connect(tin).connect(level);
    lfo(9.3, 0.22, tinAm.gain, 'triangle');
    lfo(15.7, 0.14, tinAm.gain);
    const wash = gain(0.022);
    src('pink').connect(filt('highpass', 1100, 0.5)).connect(filt('lowpass', 6500, 0.5)).connect(wash).connect(level);
    // drips off the roof's edge land on a drum (scheduled in update)
    const drips = gain(1);
    drips.connect(filt('lowpass', 4200)).connect(level);
    this.drips = drips;

    // The sodium lamp's buzz: mains harmonics, faint, a slow waver.
    const buzz = gain(0.011);
    for (const [f, g] of [
      [100, 0.6],
      [200, 0.3],
      [300, 0.12],
    ] as const) {
      const o = reg(ctx.createOscillator());
      o.frequency.value = f;
      o.connect(gain(g)).connect(buzz);
      o.start(t);
    }
    const buzzLp = filt('lowpass', 700, 0.5);
    buzz.connect(buzzLp).connect(level);
    lfo(0.4, 0.15, buzz.gain);

    level.gain.setValueAtTime(0, t);
    level.gain.setTargetAtTime(1, t, 0.5);
  }

  /**
   * The cat: a thin two-note call, the pitch rising then falling, a sawtooth through a formant band that
   * sweeps with it, muffled and small (it is across the yard, and it is not talking to you).
   */
  private miaow(t: number): void {
    const ctx = this.mx.ctx as AudioContext;
    const level = this.level;
    if (!level) return;
    const dur = r(0.42, 0.7);
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    const f0 = r(480, 620);
    const peak = f0 * r(1.35, 1.6);
    const curve = new Float32Array(24);
    for (let i = 0; i < 24; i++) {
      const u = i / 23;
      curve[i] = u < 0.35 ? f0 + (peak - f0) * Math.sin((u / 0.35) * Math.PI * 0.5) : peak - (peak - f0 * 0.8) * Math.pow((u - 0.35) / 0.65, 1.3);
    }
    osc.frequency.setValueCurveAtTime(curve, t, dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 2.2;
    bp.frequency.setValueAtTime(f0 * 2.1, t);
    bp.frequency.linearRampToValueAtTime(peak * 2.4, t + dur * 0.35);
    bp.frequency.linearRampToValueAtTime(f0 * 1.6, t + dur);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2600;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.028, t + 0.06);
    g.gain.setValueAtTime(0.028, t + dur * 0.55);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(bp).connect(lp).connect(g).connect(level);
    osc.start(t);
    osc.stop(t + dur + 0.05);
    osc.onended = () => {
      osc.disconnect();
      bp.disconnect();
      lp.disconnect();
      g.disconnect();
    };
  }

  /** The yard comes up (builds the beds when the context is ready; `update` tries again until it is). */
  start(): void {
    this.on = true;
    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    if (!this.level && this.mx.ready && this.mx.running) this.build();
    else if (this.level && this.mx.ctx) this.level.gain.setTargetAtTime(1, this.mx.ctx.currentTime, 0.5);
    this.eventClock = r(4, 9);
  }

  update(dt: number): void {
    if (!this.on) return;
    const mx = this.mx;
    if (!mx.ready || !mx.running) return;
    if (!this.level) this.build();
    const ctx = mx.ctx as AudioContext;
    // drips off the roof's edge, in little runs
    this.dripClock -= dt;
    if (this.dripClock <= 0 && this.drips) {
      this.dripClock = Math.random() < 0.3 ? r(0.08, 0.2) : r(0.5, 2.4);
      ringTick(mx, this.drips, ctx.currentTime + 0.01, r(1500, 3200), r(8, 16), r(0.04, 0.11), r(0.05, 0.1));
    }
    // the cat, once in a while, sometimes twice over
    this.catClock -= dt;
    if (this.catClock <= 0) {
      this.catClock = r(16, 40);
      this.miaow(ctx.currentTime + 0.02);
      if (Math.random() < 0.4) this.miaow(ctx.currentTime + r(0.9, 1.4));
    }
    // a dog two streets off, a far siren: sparse, never two close together
    this.eventClock -= dt;
    if (this.eventClock <= 0) {
      this.eventClock = r(11, 26);
      const e: YardEvent = Math.random() < 0.6 ? 'dog' : 'farSiren';
      this.lastEvent = e;
      this.events(e);
    }
  }

  /** The page closes: the beds fade out and are torn down after. */
  stop(): void {
    if (!this.on) return;
    this.on = false;
    const ctx = this.mx.ctx;
    if (this.level && ctx) {
      this.level.gain.cancelScheduledValues(ctx.currentTime);
      this.level.gain.setTargetAtTime(0, ctx.currentTime, 0.3);
      this.stopTimer = setTimeout(() => {
        this.stopTimer = null;
        this.teardown();
      }, 1400);
    } else this.teardown();
  }

  private teardown(): void {
    for (const n of this.nodes) {
      try {
        if (n instanceof OscillatorNode || n instanceof AudioBufferSourceNode) n.stop();
      } catch {
        // already stopped
      }
      n.disconnect();
    }
    this.nodes = [];
    this.level = null;
    this.drips = null;
  }

  dispose(): void {
    this.on = false;
    if (this.stopTimer) clearTimeout(this.stopTimer);
    this.stopTimer = null;
    this.teardown();
  }
}
